/**
 * Camera + barcode helpers for crew scanning.
 * Android Chrome lacks BarcodeDetector; ZXing is the cross-browser decoder.
 */

/** Suppress repeated reads of the same code within this window (ms). */
export const SCAN_DEBOUNCE_MS = 1500;

export type CameraErrorKind =
  | 'permission_denied'
  | 'no_camera'
  | 'camera_in_use'
  | 'unsupported'
  | 'overconstrained'
  | 'unknown';

export function classifyCameraError(err: unknown): { kind: CameraErrorKind; message: string } {
  const name =
    err && typeof err === 'object' && 'name' in err ? String((err as { name: string }).name) : '';

  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return {
      kind: 'permission_denied',
      message:
        'Camera permission denied. Allow camera access in browser settings, or use manual entry below.',
    };
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return {
      kind: 'no_camera',
      message: 'No camera found on this device. Use manual entry below.',
    };
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
    return {
      kind: 'camera_in_use',
      message:
        'Camera is already in use by another app. Close it and try again, or use manual entry below.',
    };
  }
  if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
    return {
      kind: 'overconstrained',
      message: 'Requested camera is unavailable. Try switching cameras, or use manual entry below.',
    };
  }
  if (name === 'SecurityError' || name === 'TypeError') {
    return {
      kind: 'unsupported',
      message:
        'Camera access is blocked in this context. Use HTTPS and try again, or use manual entry below.',
    };
  }

  if (
    !name &&
    (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia)
  ) {
    return {
      kind: 'unsupported',
      message: 'This browser cannot access the camera. Use manual entry below.',
    };
  }

  return {
    kind: 'unknown',
    message: 'Could not start the camera. Use manual entry below.',
  };
}

export type VideoDevice = { deviceId: string; label: string };

/** After permission is granted, enumerate cameras for driver switch UI. */
export async function listVideoInputDevices(): Promise<VideoDevice[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === 'videoinput')
    .map((d, i) => ({
      deviceId: d.deviceId,
      label: d.label?.trim() || `Camera ${i + 1}`,
    }));
}

export type ContinuousScanHandle = {
  stop: () => void;
};

/**
 * Continuous barcode decode via ZXing.
 * Keeps facingMode: environment by default; pass deviceId after the driver switches cameras.
 */
export async function startZxingContinuousScan(options: {
  video: HTMLVideoElement;
  deviceId?: string | null;
  onDetected: (code: string) => void;
  /** Called when debounce suppresses a repeated read (onDetected is not called). */
  onDuplicateIgnored?: (code: string) => void;
}): Promise<{ handle: ContinuousScanHandle; devices: VideoDevice[] }> {
  const { BrowserMultiFormatReader } = await import('@zxing/browser');
  const reader = new BrowserMultiFormatReader(undefined, {
    delayBetweenScanAttempts: 250,
    delayBetweenScanSuccess: SCAN_DEBOUNCE_MS,
  });

  const constraints: MediaStreamConstraints = {
    audio: false,
    video: options.deviceId
      ? { deviceId: { exact: options.deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }
      : {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
  };

  options.video.setAttribute('playsinline', 'true');
  options.video.muted = true;

  let lastCode = '';
  let lastAt = 0;
  let stopped = false;

  const onResult = (result: { getText(): string } | undefined) => {
    if (stopped || !result) return;
    const code = result.getText()?.trim();
    if (!code) return;
    const now = Date.now();
    if (code === lastCode && now - lastAt < SCAN_DEBOUNCE_MS) {
      options.onDuplicateIgnored?.(code);
      return;
    }
    lastCode = code;
    lastAt = now;
    options.onDetected(code);
  };

  let controls;
  try {
    controls = await reader.decodeFromConstraints(constraints, options.video, (result, _err) => {
      onResult(result ?? undefined);
    });
  } catch (err) {
    // Exact deviceId can fail on some Android builds — retry with environment.
    if (options.deviceId) {
      controls = await reader.decodeFromConstraints(
        {
          audio: false,
          video: { facingMode: { ideal: 'environment' } },
        },
        options.video,
        (result) => onResult(result ?? undefined),
      );
    } else {
      throw err;
    }
  }

  const devices = await listVideoInputDevices();

  return {
    devices,
    handle: {
      stop: () => {
        stopped = true;
        try {
          controls.stop();
        } catch {
          /* already stopped */
        }
        const stream = options.video.srcObject as MediaStream | null;
        stream?.getTracks().forEach((t) => t.stop());
        options.video.srcObject = null;
      },
    },
  };
}
